import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ListStyleEnum } from '@zaoblako/shared/common/enums/list-style.enum';
import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { BudgetRequestFactAnalyticsItemInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-fact-analytics-item.dto';
import { BudgetRequestFactAnalyticsQueryInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-fact-analytics-query.dto';
import type { BudgetRequestCreateInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request.create.dto';
import { BudgetRequestFormDto } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request.form.dto';
import type { BudgetRequestItemFindInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/item/budget-request-item-find.dto';
import { BudgetRequestTypeEnum } from '@zaoblako/shared/modules/task/enum/budget-request-type.enum';
import { BudgetRequestStatusTypeEnum } from '@zaoblako/shared/modules/task/enum/budget.request.status.type.enum';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { BudgetRequestService } from '@zaoblako/web/core/services/budget/budget-request.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ApprovalSchemeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/approval-scheme-single-select/approval-scheme-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { ProjectMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/project-multi-select/project-multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { TogglerIconTableComponent } from '@zaoblako/web/shared/dumbs/toggler-icon-table/toggler-icon-table.component';
import { UserCardPreviewComponent } from '@zaoblako/web/shared/dumbs/user-card-preview/user-card-preview.component';
import { BudgetRequestTypeNamePipe } from '@zaoblako/web/shared/pipes/budget-request-type-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { KeyValueFindPipe } from '@zaoblako/web/shared/pipes/key-value-find.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import * as moment from 'moment-timezone';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'crm-budget-task-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    JoinPipe,
    SingleSelectComponent,
    DatepickerComponent,
    ProjectMultiSelectComponent,
    ButtonModule,
    InputNumberModule,
    FormsModule,
    UserCardPreviewComponent,
    GoBackButtonComponent,
    InputTextComponent,
    MultiSelectComponent,
    OrderByPipe,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    ApprovalSchemeSingleSelectComponent,
    UserMultiSelectComponent,
    CheckboxModule,
    KeyValueFindPipe,
    CostCenterSingleSelectComponent,
    SelectButtonModule,
    TooltipModule,
    TogglerIconTableComponent,
    BudgetRequestTypeNamePipe
  ],
  templateUrl: './budget-task-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BudgetTaskFormComponent
  extends BaseFormComponent<BudgetRequestCreateInterface>
  implements OnChanges, OnInit
{
  @Input() public task = <BudgetRequestCreateInterface>{};
  public dto = BudgetRequestFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';

  @Input() public schemes: ApprovalSchemeFindInterface[] = [];
  @Input() public companies: CompanyFindInterface[] = [];
  @Input() public directions: DirectionFindInterface[] = [];
  @Input() public dealGroups: DealsGroupFindInterface[] = [];
  @Input() public userGroups: UserGroupFindInterface[] = [];
  @Input() public projects: ProjectFindInterface[] = [];

  public factAnalytics: BudgetRequestFactAnalyticsItemInterface[];
  public directionLoading: boolean;
  public directionMonths: string[];
  public directionColumns: CrmTableColumn[];

  public sort = 'direction.name:string';

  public isShowAddDirection = false;

  public selectedDirections: DirectionFindInterface[] = [];

  public openedDirectionIds: Record<number, boolean> = {};
  public selectedDirection: BudgetRequestItemFindInterface[] = [];

  public types = Object.values(BudgetRequestTypeEnum);

  public listOptions: { icon: string; label: string; value: ListStyleEnum }[] = [
    { icon: 'pi pi-sitemap', label: 'Иерархия', value: ListStyleEnum.HIERARCHY },
    { icon: 'pi pi-list', label: 'Плоский', value: ListStyleEnum.FLAT }
  ];

  public selectListFilter: ListStyleEnum = ListStyleEnum.FLAT;

  public get getCountDirections(): number {
    let count = 0;
    const parseDirection = (directions: DirectionFindInterface[]) => {
      directions.forEach((direction) => {
        if (!direction.group) {
          count++;
        }

        if (direction.children?.length) {
          parseDirection(direction.children);
        }
      });
    };

    parseDirection(this.directions);
    return count;
  }

  public get isSelectAllDirections(): boolean {
    return this.task.items?.length <= this.selectedDirection?.length;
  }

  public get ListStyleEnum() {
    return ListStyleEnum;
  }

  public get BudgetRequestStatusTypeEnum() {
    return BudgetRequestStatusTypeEnum;
  }

  public get BudgetRequestTypeEnum() {
    return BudgetRequestTypeEnum;
  }

  public constructor(
    private readonly budgetRequestService: BudgetRequestService,
    public readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.loadFactAnalytics();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.directions?.currentValue) {
      this.changeShowDirectionsSelect();
    }
    if (changes.task?.currentValue) {
      if (this.task?.items?.length) {
        this.task.items = this.task.items.filter((item) => item.amount);
      }
    }
  }

  public loadFactAnalytics() {
    if (this.task.items?.length) {
      this.directionLoading = true;
      this.cdRef.markForCheck();

      const params: BudgetRequestFactAnalyticsQueryInterface = {
        directionIds: this.task.items?.map((item) => item?.direction?.id) ?? [],
        unitId: this.task.unit?.id,
        dealIds: this.task.dealsGroup?.deals?.map((deal) => deal?.id) ?? []
      };
      this.budgetRequestService.getFactAnalyticsBySettings(params).subscribe({
        next: (factAnalytics) => {
          this.factAnalytics = factAnalytics;
          this.directionMonths = Object.keys(factAnalytics[0]?.monthAmount);
          this.directionColumns = [
            { label: 'Бюджетная статья', name: 'direction', sort: 'direction.name:string' },
            { label: 'Сумма', name: 'amount', sort: 'amount:number', class: 'w-15rem' }
          ];
          if (this.directionMonths?.length) {
            [...this.directionMonths]
              .map((month) => moment(month, 'YYYY-MM').locale('ru').format('MMM YYYY'))
              .forEach((label) => {
                this.directionColumns.push({ label });
              });

            this.directionColumns = [...this.directionColumns];
          }
          this.directionLoading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.directionLoading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public toggleAddDirection() {
    this.changeShowDirectionsSelect();
    this.isShowAddDirection = true;
    this.cdRef.markForCheck();
  }

  public addDirections() {
    if (!Array.isArray(this.task.items) || !this.selectedDirections?.length) {
      this.task.items = [];
    }

    if (this.selectedDirections?.length) {
      this.selectedDirections?.forEach((direction) => {
        if (!direction?.group && this.task.items?.findIndex((item) => item?.direction?.id === direction.id) === -1) {
          this.task.items.push({
            direction,
            amount: 0
          });
        }
      });

      this.task.items?.forEach((item) => {
        if (
          !item?.direction?.group &&
          !this.selectedDirections?.map((direction) => direction.id).includes(item.direction?.id)
        ) {
          this.task.items.splice(
            this.task.items?.findIndex(
              (item) => !this.selectedDirections?.map((direction) => direction?.id).includes(item.direction?.id)
            ),
            1
          );
        }
      });

      this.task.items = [...this.task.items];
      this.cdRef.markForCheck();
    }
    this.loadFactAnalytics();
  }

  public setAllDirections() {
    const parseDirection = (directions: DirectionFindInterface[]) => {
      directions.forEach((direction) => {
        if (!direction.group && this.task?.items?.findIndex((item) => item.direction?.id === direction.id) === -1) {
          this.task.items.push({
            direction,
            amount: 0
          });
        }

        if (direction.children?.length) {
          parseDirection(direction.children);
        }
      });
    };

    if (!Array.isArray(this.task.items)) {
      this.task.items = [];
    }

    parseDirection(this.directions);
    this.task.items = [...this.task.items];
    this.loadFactAnalytics();
  }

  public toItem(item: any): BudgetRequestItemFindInterface {
    return item as BudgetRequestItemFindInterface;
  }

  public changeAmount(item: BudgetRequestItemFindInterface, amount: number) {
    if (isNaN(amount) || !amount) {
      setTimeout(() => {
        item.amount = 0;
        this.cdRef.markForCheck();
      }, 10);
    }
    this.task.items = [...this.task.items];
  }

  public sortChange(sort: string) {
    this.sort = sort;
  }

  public changeUsersAndAuditors(scheme: ApprovalSchemeFindInterface) {
    if (!this.canDelete) {
      this.task.userAuditors = [];
      this.task.userGroupAuditors = [];
      if (scheme.userAuditors?.length) {
        this.task.userAuditors = scheme.userAuditors;
      }
      if (scheme?.userGroupAuditors?.length) {
        this.task.userGroupAuditors = scheme.userGroupAuditors;
      }
    }
  }

  public changeDate(key: KeysOfType<BudgetRequestCreateInterface, Date>, date: Date) {
    this.task[key] = moment(date).format('YYYY-MM') as unknown as Date;
  }

  public onSave(entity: BudgetRequestCreateInterface) {
    if (
      entity.items?.length &&
      !entity.items.every((item) => item.amount) &&
      this.canDelete &&
      this.task.status !== BudgetRequestStatusTypeEnum.DRAFT
    ) {
      if (!this.errors) {
        this.errors = {} as any;
      }
      this.errors.items = 'Заполните суммы бюджетных статей' as any;
      return;
    }
    this.selectedDirections = entity.items?.length ? entity.items.map((item) => item.direction) : [];
    super.onSave(entity);
  }

  private changeShowDirectionsSelect() {
    if (!Array.isArray(this.task.items) || !this.task.items?.length) {
      this.selectedDirections = [];
    } else if (this.task.items?.length && !this.selectedDirections?.length) {
      this.task.items?.forEach((item) => {
        if (!item.direction?.group) {
          this.selectedDirections.push(item.direction);
        }
      });
    } else if (this.task.items?.length && this.selectedDirections?.length) {
      this.task.items?.forEach((item) => {
        if (
          !item.direction?.group &&
          this.selectedDirections?.findIndex((direction) => item.direction?.id === direction.id) === -1
        ) {
          this.selectedDirections.push(item.direction);
        }
      });
      this.selectedDirections?.forEach((direction) => {
        if (!direction?.group && !this.task.items?.map((item) => item.direction?.id).includes(direction.id)) {
          this.selectedDirections.splice(
            this.selectedDirections?.findIndex(
              (direction) => !this.task.items?.map((item) => item.direction?.id).includes(direction.id)
            ),
            1
          );
        }
      });
    }
  }

  public toAnalytics(analytics: any): BudgetRequestFactAnalyticsItemInterface {
    return analytics as BudgetRequestFactAnalyticsItemInterface;
  }

  public fillDirectionAmount(
    factAnalytics: BudgetRequestFactAnalyticsItemInterface[],
    type: 'max' | 'previous' | 'average'
  ) {
    switch (type) {
      case 'max': {
        this.selectedDirection.forEach((item) => {
          item.amount = Math.max(
            ...Object.values(factAnalytics.find((analytics) => analytics?.id === item.direction?.id)?.monthAmount)
          );
        });
        break;
      }
      case 'previous': {
        this.selectedDirection.forEach((item) => {
          item.amount = Object.values(
            factAnalytics.find((analytics) => analytics?.id === item.direction?.id)?.monthAmount
          )[2];
        });
        break;
      }
      case 'average': {
        this.selectedDirection.forEach((item) => {
          const monthValues = Object.values(
            factAnalytics.find((analytics) => analytics?.id === item.direction?.id)?.monthAmount
          );
          item.amount = +(monthValues.reduce((acc, num) => num + acc, 0) / monthValues?.length).toFixed(0);
        });
        break;
      }
    }

    this.selectedDirection = [];
  }

  public selectAllDirections() {
    if (this.isSelectAllDirections) {
      this.selectedDirection = [];
    } else {
      this.selectedDirection = this.task?.items;
    }
  }

  public toggleDirection(id: number) {
    this.openedDirectionIds[id] = !this.openedDirectionIds[id];
  }

  public selectDirection(item: BudgetRequestItemFindInterface) {
    if (this.selectedDirection.some((_item) => _item.direction?.id === item.direction?.id)) {
      this.selectedDirection = this.selectedDirection.filter((_item) => _item.direction?.id !== item.direction?.id);
    } else {
      this.selectedDirection = this.selectedDirection.concat(item);
    }
  }
}
